import type { RefObject } from 'react';
import {
  EXPORT_MARGIN_PX,
  type RecordedSvgOptions,
  composeRecordedSvgDocument,
  directExportScale,
  recordSvg,
  renderUntilImagesSettle,
} from '@ardoq/export';
import type {
  AqBlocks,
  ExportRenderOptions,
} from 'tabview/visualizations/aq-blocks/types';

/**
 * SVG is resolution independent, so it is recorded at world scale; the whitespace
 * comes from the document composition, which measures the drawn content.
 */
export const SVG_EXPORT_OPTIONS: ExportRenderOptions = { scale: 1, margin: 0 };

type LooseDrawImage = (image: CanvasImageSource, ...coords: number[]) => void;

/**
 * Wraps `drawImage` so a caller can await the images a render pass touched.
 * `drawImage` is overloaded per argument count; rather than re-declaring every
 * form, the wrapper forwards its arguments verbatim through a single loosely
 * typed boundary.
 */
const trackDrawnImages = (
  ctx: OffscreenCanvasRenderingContext2D
): Set<HTMLImageElement> => {
  const images = new Set<HTMLImageElement>();
  const drawImage = ctx.drawImage.bind(ctx) as LooseDrawImage;
  const tracked: LooseDrawImage = (image, ...coords) => {
    if (image instanceof HTMLImageElement) {
      images.add(image);
    }
    drawImage(image, ...coords);
  };
  ctx.drawImage = tracked as OffscreenCanvasRenderingContext2D['drawImage'];

  return images;
};

/**
 * The render options for the diagram's *current* bounds: the scale is clamped
 * against the canvas limits, so it has to be derived from the bounds it will
 * actually be applied to.
 */
export const currentExportOptions = (
  element: AqBlocks
): ExportRenderOptions | null => {
  const contentSize = element.getExportContentSize();
  if (!contentSize) {
    return null;
  }

  return {
    scale: directExportScale(
      { width: contentSize[0], height: contentSize[1] },
      window.devicePixelRatio,
      EXPORT_MARGIN_PX
    ),
    margin: EXPORT_MARGIN_PX,
  };
};

/**
 * The whole diagram rendered to an offscreen canvas, at up to the device pixel
 * ratio and within the browser's canvas limits. The canvas is transparent; the
 * export pipeline composites it onto the background and appends the legend.
 *
 * The scale drives the aq-blocks projection, which is also what its renderers
 * derive level of detail from, so a diagram large enough to be clamped below
 * roughly 0.43 exports with faded labels — the same detail the on-screen view
 * drops at that zoom. yFiles keeps the two apart (`exporter.zoom = 1`); aq-blocks
 * has one scale. SVG is unaffected: it records at scale 1.
 *
 * @returns null when the diagram stopped being renderable, so the caller reports
 * a failure rather than saving an empty image.
 */
const createExportCanvas = async (
  element: AqBlocks
): Promise<OffscreenCanvas | null> => {
  const initialOptions = currentExportOptions(element);
  const initialSize = initialOptions && element.getExportSize(initialOptions);
  if (!initialSize) {
    return null;
  }

  const canvas = new OffscreenCanvas(initialSize[0], initialSize[1]);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return null;
  }

  await document.fonts.ready;
  const drawnImages = trackDrawnImages(ctx);

  const outcome = await renderUntilImagesSettle(() => {
    // Re-derive both per pass: a relayout inside the settle window (an
    // expand/collapse landing mid-export) moves the diagram bounds, which changes
    // the size *and* the clamped scale. Reusing the first pass's scale against
    // grown bounds is how the canvas would exceed the limit it was clamped to.
    const options = currentExportOptions(element);
    const size = options && element.getExportSize(options);
    // The diagram stopped being renderable mid-export (its graph went away).
    // Aborting beats settling on a canvas nothing was drawn to.
    if (!options || !size) {
      return null;
    }

    // Assigning either dimension reallocates and zeroes the bitmap — the largest
    // in the app by construction — so only resize when the size actually moved.
    if (canvas.width !== size[0] || canvas.height !== size[1]) {
      canvas.width = size[0];
      canvas.height = size[1];
    }

    drawnImages.clear();
    ctx.clearRect(0, 0, size[0], size[1]);
    // renderExport reports false while item animations are still interpolating,
    // so an export started mid expand/collapse waits for the final frame rather
    // than capturing the diagram halfway.
    const isComplete = element.renderExport(ctx, options);
    return { images: drawnImages, isComplete };
  });

  return outcome === 'aborted' ? null : canvas;
};

/**
 * The whole diagram as a standalone vector SVG document, recorded by running the
 * aq-blocks renderers against a canvas-to-SVG recording context rather than a
 * real canvas.
 *
 * @returns null when the diagram stopped being renderable, so the caller reports
 * a failure rather than saving an empty document.
 */
const renderSvgDocument = async (
  element: AqBlocks,
  options: RecordedSvgOptions
): Promise<string | null> => {
  const initialSize = element.getExportSize(SVG_EXPORT_OPTIONS);
  if (!initialSize) {
    return null;
  }

  const { recorder, renderable } = await recordSvg(ctx => {
    // Same pre-check as the PNG pass: `renderExport` reports a complete render
    // after drawing nothing, so an element that stopped being renderable has to
    // be caught here or the recording settles empty and a blank document saves.
    const size = element.getExportSize(SVG_EXPORT_OPTIONS);
    if (!size) {
      return { renderable: false, isComplete: true };
    }

    return {
      renderable: true,
      isComplete: element.renderExport(ctx, SVG_EXPORT_OPTIONS),
    };
  });

  if (!renderable) {
    return null;
  }

  return composeRecordedSvgDocument(
    recorder,
    { width: initialSize[0], height: initialSize[1] },
    options
  );
};

export type FlexibleBlockDiagramExportHandlers = {
  getContainer: () => HTMLElement | null;
  createExportCanvas: () => Promise<OffscreenCanvas | null>;
  renderSvgDocument: (options: RecordedSvgOptions) => Promise<string | null>;
};

type CreateExportHandlersArgs = {
  /** the live `<aq-blocks>` element, which owns the graph and its renderers */
  elementRef: RefObject<AqBlocks | null>;
  /** the view container, from which the export pipeline picks up the legend */
  containerRef: RefObject<HTMLElement | null>;
};

/**
 * The view-pane export handlers for a Flexible Block Diagram instance. Created
 * once per view model so the identity stays stable across emissions, and reads
 * the refs on invocation rather than at creation.
 */
export const createFlexibleBlockDiagramExportHandlers = ({
  elementRef,
  containerRef,
}: CreateExportHandlersArgs): FlexibleBlockDiagramExportHandlers => ({
  getContainer: () => containerRef.current,
  createExportCanvas: async () => {
    const element = elementRef.current;
    return element ? createExportCanvas(element) : null;
  },
  renderSvgDocument: async options => {
    const element = elementRef.current;
    return element ? renderSvgDocument(element, options) : null;
  },
});
